import {
    type ComponentProps,
    type ReactNode,
    type Ref,
    useCallback,
    useState,
} from "react";
import {
    useField,
    useFormikContext,
    type FieldHelperProps,
    type FieldInputProps,
    type FieldMetaProps,
} from "formik";
import {
    Field,
    type FieldNameProps,
    pickFieldNameProps,
} from "@design-system/Field/Field";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import { Input } from "@design-system/Input/Input";
import type { InputSize } from "@design-system/Input/Input.types";
import { flattenFormError, shouldShowFieldError } from "@helpers/form";

export interface FormikFieldRenderContext<Value> {
    field: FieldInputProps<Value>;
    meta: FieldMetaProps<Value>;
    helpers: FieldHelperProps<Value>;
    invalid: boolean;
}

/** `FormikField` props besides how the field is named. */
export interface FormikFieldBaseProps<Value> {
    name: string;
    control?: "input" | "button";
    optional?: boolean;
    description?: ReactNode;
    className?: string;
    showErrorMessage?: boolean;
    children?:
        | ReactNode
        | ((context: FormikFieldRenderContext<Value>) => ReactNode);
    size?: InputSize;
    type?: ComponentProps<"input">["type"];
    placeholder?: string;
    disabled?: boolean;
    readOnly?: boolean;
    maxLength?: number;
    autoComplete?: string;
    autoFocus?: boolean;
    /**
     * Announced on the default input as `aria-required`. The native `required`
     * attribute is intentionally not set, because browser validation would
     * block Formik's submit.
     */
    required?: boolean;
    ref?: Ref<HTMLInputElement>;
}

/**
 * Named by `label` (optionally `labelHidden`) or by `aria-labelledby`. With
 * `aria-labelledby` the default input receives the id; a render child passes
 * it to its own control.
 */
export type FormikFieldProps<Value> = FormikFieldBaseProps<Value> &
    FieldNameProps;

const toInputValue = <Value,>(
    value: Value,
): ComponentProps<"input">["value"] => {
    if (typeof value === "string" || typeof value === "number") {
        return value;
    }
    if (value === undefined) {
        return undefined;
    }

    return "";
};

/**
 * Hides a field's error while the user edits it: any value change suppresses
 * it until focus leaves `rootRef`'s element or the form is submitted.
 */
const useErrorSuppressedWhileEditing = <Value,>({
    value,
    submitCount,
}: {
    value: Value;
    submitCount: number;
}) => {
    const [seenValue, setSeenValue] = useState(() => value);
    const [suppressedAtSubmitCount, setSuppressedAtSubmitCount] = useState<
        number | null
    >(null);
    if (!Object.is(seenValue, value)) {
        setSeenValue(() => value);
        setSuppressedAtSubmitCount(submitCount);
    }

    const rootRef = useCallback((node: HTMLElement | null) => {
        if (!node) {
            return undefined;
        }
        const endEditing = (event: FocusEvent) => {
            if (
                !(event.relatedTarget instanceof Node) ||
                !node.contains(event.relatedTarget)
            ) {
                setSuppressedAtSubmitCount(null);
            }
        };
        node.addEventListener("focusout", endEditing);
        return () => node.removeEventListener("focusout", endEditing);
    }, []);

    return {
        isSuppressed: suppressedAtSubmitCount === submitCount,
        rootRef,
    };
};

/**
 * Formik state for one field plus `FormikField`'s error rules: `invalid`
 * after blur or submit but not while the user is changing the value, and the
 * flattened message unless hidden. For fields that draw their own chrome
 * around a control instead of `Field`; pass `rootRef` to the element wrapping
 * the control so leaving it shows the error again.
 */
export const useFormikFieldState = <Value,>({
    name,
    showErrorMessage = true,
}: {
    name: string;
    showErrorMessage?: boolean;
}) => {
    const [field, meta, helpers] = useField<Value>(name);
    const { submitCount } = useFormikContext<Record<string, Value>>();
    const { isSuppressed, rootRef } = useErrorSuppressedWhileEditing({
        value: field.value,
        submitCount,
    });
    const invalid =
        !isSuppressed &&
        shouldShowFieldError({
            error: meta.error,
            touched: meta.touched,
            submitCount,
        });
    const error =
        invalid && showErrorMessage && meta.error !== undefined
            ? flattenFormError(meta.error)
            : undefined;

    return { field, meta, helpers, invalid, error, rootRef };
};

export const FormikField = <Value,>(props: FormikFieldProps<Value>) => {
    const {
        name,
        control,
        optional,
        description,
        className,
        showErrorMessage,
        children,
        size,
        type,
        placeholder,
        disabled,
        readOnly,
        maxLength,
        autoComplete,
        autoFocus,
        required,
        ref,
    } = props;
    const {
        field,
        meta,
        helpers,
        invalid: isInvalid,
        error,
        rootRef,
    } = useFormikFieldState<Value>({ name, showErrorMessage });

    let fieldControl: ReactNode;
    if (typeof children === "function") {
        fieldControl = children({
            field,
            meta,
            helpers,
            invalid: isInvalid,
        });
    } else if (children !== undefined) {
        fieldControl = children;
    } else {
        fieldControl = (
            <Input
                {...field}
                ref={ref}
                value={toInputValue(field.value)}
                invalid={isInvalid}
                aria-required={required ? true : undefined}
                {...ariaLabelledbyProps(props["aria-labelledby"])}
                size={size}
                type={type}
                placeholder={placeholder}
                disabled={disabled}
                readOnly={readOnly}
                maxLength={maxLength}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
            />
        );
    }

    return (
        <Field
            ref={rootRef}
            {...pickFieldNameProps(props)}
            control={control}
            optional={optional}
            description={description}
            error={error}
            invalid={isInvalid}
            className={className}
        >
            {fieldControl}
        </Field>
    );
};
